import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Zap, BookOpen, BarChart2, Shuffle, Bot } from "lucide-react";

const ALL_FEATURES = [
  { icon: Zap, text: "Unlimited practice tests across all topics" },
  { icon: Shuffle, text: "Smart question selection — prioritises weak areas" },
  { icon: BookOpen, text: "Full access to all question banks" },
  { icon: BarChart2, text: "Detailed per-session and all-time review" },
  { icon: CheckCircle2, text: "Track every attempt with correct / incorrect history" },
  { icon: Bot, text: "Aira AI tutor — ask questions about any topic in real time" },
];

export function PaywallPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckout = async () => {
    if (!user) { window.location.href = "/login"; return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.$id, userEmail: user.email, plan: "pro" }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setError("Could not connect to payment service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
          <Zap className="w-3.5 h-3.5" />
          You've used your 2 free practice tests
        </div>

        <h1 className="text-3xl font-bold mb-3">Unlock Unlimited Access</h1>
        <p className="text-muted-foreground mb-10">
          Everything you need to ace your exam — including Aira AI. Cancel any time.
        </p>

        {error && <p className="text-sm text-red-500 mb-6">{error}</p>}

        <div className="bg-card border-2 border-violet-500 rounded-2xl p-6 flex flex-col text-left relative overflow-hidden">
          <div className="absolute top-4 right-4 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-500 text-white">
            Includes AI
          </div>

          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-violet-500 mb-1">AI Platform</p>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold">$9.99</span>
              <span className="text-muted-foreground mb-1">/ month</span>
            </div>
          </div>

          <ul className="space-y-3 flex-1 mb-8">
            {ALL_FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          <Button
            className="w-full h-11 font-semibold bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white border-0"
            onClick={handleCheckout}
            disabled={loading}
          >
            {loading ? "Redirecting…" : "Get Started — $9.99/mo"}
          </Button>
        </div>

        <button
          className="mt-8 text-sm text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => { window.location.href = "/subjects"; }}
        >
          Go back
        </button>
      </div>
    </div>
  );
}
